import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FaBuysellads,
  FaShieldAlt,
  FaTools,
  FaGem,
  FaArrowLeft,
  FaArrowRight,
  FaCheckCircle,
} from "react-icons/fa";
import { IoIosVideocam } from "react-icons/io";
import { TbSettingsCog } from "react-icons/tb";

const featuresData = [
  {
    id: "01",
    icon: <FaBuysellads className="text-2xl text-[#D97706]" />,
    title: "Dynamic Ads",
    badge: "+40% Revenue Boost",
    description:
      "Maximize your existing inventory revenue and introduce new opportunities to increase revenues streams through our state of the art Dynamic Ads solution and increase your revenue upto 40%.",
    color: "from-[#D97706] to-[#E8882D]",
  },
  {
    id: "02",
    icon: <IoIosVideocam className="text-2xl text-[#E8882D]" />,
    title: "Video Uploading",
    badge: "Plug & Play Solution",
    description:
      "We provide a premium plug-and-play video solution that receives and showcases original, topic-specific video content paired with in-stream programmatic advertising from the world's leading demand sources.",
    color: "from-[#E8882D] to-[#D97706]",
  },
  {
    id: "03",
    icon: <FaShieldAlt className="text-2xl text-[#D97706]" />,
    title: "Shielding",
    badge: "Pre-Violation Protection",
    description:
      "Myad360's publisher solutions proactively detect pre-violations to protect our publishers from potential bans. Through our advanced proprietary optimization technologies, analytics management tools, and tailored services, we ensure your content stays safe, compliant, and optimized for maximum performance.",
    color: "from-[#D97706] to-[#EA580C]",
  },
  {
    id: "04",
    icon: <TbSettingsCog className="text-2xl text-[#E8882D]" />,
    title: "Custom development",
    badge: "Custom Players & Plugins",
    description:
      "Our team tunes into your needs to help you build the right platform for managing your advertising by creating a custom video player, flash to htm5 video player and WordPress plugins.",
    color: "from-[#D97706] to-[#D97706]",
  },
  {
    id: "05",
    icon: <FaTools className="text-2xl text-[#D97706]" />,
    title: "Tools",
    badge: "iAdX Earnings Guarantee",
    description:
      "MyAd360 guarantees the best results – or your money back. No upfront fees, no long-term contracts. If we don't boost your ad revenue within 60 days, you pay nothing.",
    color: "from-[#D97706] to-[#D97706]",
  },
  {
    id: "06",
    icon: <FaGem className="text-2xl text-[#D97706]" />,
    title: "Supported ad formats",
    badge: "Cross-Platform Formats",
    description:
      "At Myad360, we offer a wide range of high-performance ad formats to help you maximize revenue across your digital inventory. From display ads to video placements, our solutions are tailored to fit any publisher's needs, delivering seamless experiences and higher returns.",
    color: "from-[#EA580C] to-[#D97706]",
  },
];

// Tracks which card is centered in the scroll track so the "02 / 06"
// counter and future keyboard/indicator logic stay in sync with real
// scroll position instead of a slider library's internal state.
function useScrollActiveIndex(containerRef, itemCount) {
  const [active, setActive] = useState(0);

  const handle = useCallback(() => {
    const el = containerRef.current;
    if (!el || !el.children[0]) return;

    const maxScrollLeft = el.scrollWidth - el.clientWidth;

    // Near the very end of scroll (within 4px), always report the last card —
    // rounding against cardWidth alone can never reach the final index because
    // the last snap point sits slightly short of index * cardWidth.
    if (maxScrollLeft <= 0 || el.scrollLeft >= maxScrollLeft - 4) {
      setActive(itemCount - 1);
      return;
    }

    const cardWidth = el.children[0].getBoundingClientRect().width + 24; // gap-6
    const index = Math.round(el.scrollLeft / cardWidth);
    setActive(Math.min(itemCount - 1, Math.max(0, index)));
  }, [containerRef, itemCount]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    handle();
    el.addEventListener("scroll", handle, { passive: true });
    window.addEventListener("resize", handle);
    return () => {
      el.removeEventListener("scroll", handle);
      window.removeEventListener("resize", handle);
    };
  }, [containerRef, handle]);

  return active;
}

// On mobile we want exactly one card centered in the viewport, with the
// track edge-padded so the first and last cards can actually reach center.
// On desktop (md+) several cards sit side by side, so no centering padding
// is needed there.
function useCenterPadding(containerRef) {
  const [pad, setPad] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    const update = () => {
      if (!el || !el.children[0]) return;
      if (window.innerWidth >= 768) {
        setPad(0);
        return;
      }
      const cardWidth = el.children[0].getBoundingClientRect().width;
      setPad(Math.max((window.innerWidth - cardWidth) / 2, 16));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [containerRef]);

  return pad;
}

export default function ServicesSection() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.15 });
  const trackRef = useRef(null);
  const active = useScrollActiveIndex(trackRef, featuresData.length);
  const centerPad = useCenterPadding(trackRef);
  const shouldReduceMotion = useReducedMotion();

  const isFirst = active === 0;
  const isLast = active === featuresData.length - 1;

  const scrollByCard = (direction) => {
    if ((direction < 0 && isFirst) || (direction > 0 && isLast)) return;
    const el = trackRef.current;
    if (!el || !el.children[0]) return;
    const cardWidth = el.children[0].getBoundingClientRect().width + 24;
    el.scrollBy({ left: direction * cardWidth, behavior: "smooth" });
  };

  return (
    <section
      id="featuresID"
      ref={ref}
      className="relative py-20 md:py-28 overflow-hidden border-t border-[#F5DFC5]"
      style={{
        background:
          "radial-gradient(90% 70% at 80% 10%, #FEF3E5 0%, transparent 55%), radial-gradient(80% 60% at 10% 90%, #FFF4E5 0%, transparent 55%), #FFFBF5",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

        .myad-heading { font-family: 'Space Grotesk', sans-serif; }
        .myad-body { font-family: 'Inter', sans-serif; }
        .myad-mono { font-family: 'JetBrains Mono', monospace; }

        .myad-track {
          scroll-snap-type: x mandatory;
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .myad-track::-webkit-scrollbar { display: none; }

        /* One card centered per view on mobile; from md up, multiple cards
           sit side by side so snapping to "start" reads more naturally. */
        .myad-card { scroll-snap-align: start; }
        @media (max-width: 767px) {
          .myad-card { scroll-snap-align: center; }
        }

        .myad-curl {
          stroke-dasharray: 90;
          stroke-dashoffset: 90;
          transition: stroke-dashoffset 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .myad-card:hover .myad-curl,
        .myad-card:focus-within .myad-curl {
          stroke-dashoffset: 0;
        }

        @media (prefers-reduced-motion: reduce) {
          .myad-curl { transition: none; }
        }
      `}</style>

      {/* Ambient glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[380px] opacity-[0.08] blur-[130px] pointer-events-none" style={{ background: "radial-gradient(circle, #FDDBB8, transparent 70%)" }} />



      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-6 md:mb-20 gap-4 md:gap-6"
        >
          <div className="max-w-2xl text-left">
            <div className="flex items-center justify-center gap-4 mb-5">
              <span
                  className="hidden sm:inline-block h-[2px] flex-1"
                  style={{
                    background: "linear-gradient(to left, #EA580C, transparent)",
                  }}
                />
              <span className="myad-mono inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#92400E] whitespace-nowrap">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D97706] opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#D97706]" />
                </span>
                Live across every placement
              </span>
              <span
                  className="hidden sm:inline-block h-[2px] flex-1"
                  style={{
                    background: "linear-gradient(to right, #EA580C, transparent)",
                  }}
                />
            </div>

            <h2 className="myad-heading text-3xl sm:text-4xl lg:text-[3.25rem] font-bold text-slate-900 tracking-tight leading-[1.1]">
              Myad360{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D97706] via-[#E8882D] to-[#D97706]">
                Our
              </span>{" "}
              Features
            </h2>

            <p className="myad-body text-slate-600 text-sm sm:text-base leading-relaxed mt-4 max-w-xl">
              Explore our full suite of programmatic ad tools with smooth animated transitions.
            </p>
          </div>

          <div className="flex items-center gap-4 self-end">

            <div className="flex items-center gap-2">
              <button
                onClick={() => scrollByCard(-1)}
                disabled={isFirst}
                className="w-11 h-11 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-[#D97706] hover:text-white hover:border-[#D97706] flex items-center justify-center transition-all duration-300 shadow-sm disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-slate-600 disabled:hover:border-slate-200"
                aria-label="Previous feature"
              >
                <FaArrowLeft className="text-sm" />
              </button>
              <button
                onClick={() => scrollByCard(1)}
                disabled={isLast}
                className="w-11 h-11 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-[#D97706] hover:text-white hover:border-[#D97706] flex items-center justify-center transition-all duration-300 shadow-sm disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-slate-600 disabled:hover:border-slate-200"
                aria-label="Next feature"
              >
                <FaArrowRight className="text-sm" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Cards track — native scroll-snap instead of a slider library */}
        <div
          ref={trackRef}
          tabIndex={0}
          role="list"
          aria-label="Myad360 features"
          className="myad-track flex gap-6 overflow-x-auto overflow-y-visible pt-4 pb-8 md:mx-0 md:px-0 focus:outline-none"
          style={{ paddingLeft: centerPad, paddingRight: centerPad }}
        >
          {featuresData.map((item, i) => (
            <motion.article
              key={item.id}
              role="listitem"
              initial={{ opacity: 0, y: 36 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.55,
                delay: shouldReduceMotion ? 0 : 0.12 + i * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
              whileHover={shouldReduceMotion ? {} : { y: -8 }}
              className="myad-card group relative bg-white rounded-[2rem] rounded-tl-[3.25rem] p-6 sm:p-7 md:p-8 border border-slate-200/90 hover:border-[#D97706]/40 focus-within:border-[#D97706]/40 transition-colors duration-300 shrink-0 w-[82vw] xs:w-[280px] sm:w-[300px] md:w-[330px] flex flex-col justify-between overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_20px_40px_-24px_rgba(217,119,6,0.25)]"
            >
              <div
                className={`absolute top-0 left-8 right-8 h-1 rounded-b-full bg-gradient-to-r ${item.color}`}
              />

              <div>
                <div className="flex items-center justify-between gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-[#FEF3E5] border border-[#FDE8CD] flex items-center justify-center group-hover:bg-[#D97706] transition-colors duration-300">
                    {React.cloneElement(item.icon, {
                      className:
                        "text-2xl text-[#D97706] group-hover:text-white transition-colors duration-300",
                    })}
                  </div>
                  <span className="myad-mono text-[10px] tracking-wide font-medium px-2.5 py-1 rounded-full bg-[#FEF3E5] text-[#92400E] border border-[#FDE8CD]">
                    {item.badge}
                  </span>
                </div>

                <h3 className="myad-heading text-lg font-semibold text-slate-900 mb-2.5 group-hover:text-[#D97706] transition-colors">
                  {item.title}
                </h3>
                <p className="myad-body text-slate-600 text-[13px] sm:text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>

              <div className="mt-8 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-semibold text-[#D97706]">
                <span className="flex items-center gap-1.5">
                  <FaCheckCircle className="text-[#D97706]" /> MyAd360 Verified
                </span>
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </div>

              {/* Hover flourish — a small curl that echoes the wave above */}
              <svg
                className="absolute -bottom-2 -left-2 w-16 h-16 pointer-events-none"
                viewBox="0 0 64 64"
                aria-hidden="true"
              >
                <path
                  d="M4 4 C 4 40, 24 60, 60 60"
                  fill="none"
                  stroke="#D97706"
                  strokeWidth="2"
                  strokeLinecap="round"
                  className="myad-curl"
                />
              </svg>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}